(() => {
  const el = (tag, text, attrs = {}) => {
    const node = document.createElement(tag);
    if (text !== null) node.textContent = text;
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };
  const displayTitle = title => String(title || '').replace(/^\s*PART\s+\d+\s*(?:[·•:—–-]\s*)?/i, '').trim().replace(/^rohs$/i, 'ROHS');
  const moduleTitleFromHandle = handle => String(handle || '').replace(/[-_]+/g, ' ').replace(/\b[a-z]/g, letter => letter.toUpperCase()).trim();
  const icon = name => {
    const paths = {
      overview:'M3 10 12 3 21 10V21H15V14H9V21H3Z',
      knowledge:'M12 7C9 5 6 4 3 5V20C6 19 9 20 12 22C15 20 18 19 21 20V5C18 4 15 5 12 7ZM12 7V22',
      profiles:'M6 9V3H18V9M6 18H4V9H20V18H18M6 14H18V21H6ZM17 12H18',
      documents:'M14 2H6V22H20V8ZM14 2V8H20M9 13H16M9 17H16',
      arrow:'M4 12H20M13 5L20 12L13 19'
    };
    const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
    svg.setAttribute('viewBox','0 0 24 24');
    svg.setAttribute('class','wiki-icon');
    svg.setAttribute('aria-hidden','true');
    const path = document.createElementNS(svg.namespaceURI,'path');
    path.setAttribute('d',paths[name] || paths.documents);
    svg.append(path);
    return svg;
  };
  function removePartPrefix(heading) {
    const prefix = (heading.textContent || '').match(/^\s*PART\s+\d+\s*(?:[·•:—–-]\s*)?/i)?.[0];
    if (!prefix) return;
    let remaining = prefix.length;
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    let textNode;
    while (remaining && (textNode = walker.nextNode())) {
      const value = textNode.textContent || '';
      const remove = Math.min(remaining, value.length);
      textNode.textContent = value.slice(remove);
      remaining -= remove;
    }
  }
  async function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const main = root.querySelector('[data-wiki-main]');
    try {
      const templates = [...root.querySelectorAll('template[data-wiki-node]')];
      let next = root.querySelector('[data-wiki-next]')?.getAttribute('href');
      const visited = new Set();
      while (next) {
        const url = new URL(next, location.href);
        if (url.origin !== location.origin || visited.has(url.href) || visited.size >= 100) throw new Error('pagination');
        visited.add(url.href);
        const response = await fetch(url.href, {credentials:'same-origin'});
        if (!response.ok) throw new Error('network');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = [...doc.querySelectorAll('[data-wiki]')].find(r => r.dataset.section === root.dataset.section);
        if (!source) throw new Error('section');
        templates.push(...source.querySelectorAll('template[data-wiki-node]'));
        next = source.querySelector('[data-wiki-next]')?.getAttribute('href');
      }
      const all = new Map(templates.map(t => [t.dataset.wikiNode, {...t.dataset, handle:t.dataset.wikiNode, template:t}]));
      if (all.size !== Number(root.dataset.total)) throw new Error('count');
      const visible = n => {
        const seen = new Set();
        while (n) {
          if (n.visible !== 'true' || seen.has(n.handle)) return false;
          seen.add(n.handle);
          if (!n.parent) return true;
          const parent = all.get(n.parent);
          if (!parent || parent.module !== n.module) return false;
          n = parent;
        }
        return false;
      };
      const nodes = [...all.values()].filter(n => visible(n));
      let moduleLabels = {knowledge:'FDM Printing Guide',profiles:'Printing Parameters',documents:'Certificates & Documents'};
      try {moduleLabels = {...moduleLabels,...JSON.parse(root.dataset.moduleLabels || '{}')};} catch {}
      const modules = ['knowledge','profiles','documents'].map(handle => {
        const moduleNode = nodes.find(n => n.handle === handle && n.module === handle && !n.parent);
        const translatedTitle = moduleLabels[handle];
        const title = translatedTitle || moduleTitleFromHandle(handle);
        return {handle, title, moduleNode};
      });
      const byHandle = new Map(nodes.map(n => [n.handle,n]));
      const initialHandle = root.dataset.articlePage === 'true' ? nodes.find(n => n.pageId === root.dataset.pageId)?.handle || 'unavailable' : 'overview';
      const pageUrl = n => {
        if (n?.module === 'documents' && n.kind === 'group') return null;
        if (!n?.pageUrl) return null;
        const url = new URL(n.pageUrl, location.href);
        return url.origin === location.origin && /^\/pages\/[^/]+\/?$/.test(url.pathname) ? url : null;
      };
      const href = handle => {
        const url = pageUrl(byHandle.get(handle));
        if (!url) return `#wiki/${encodeURIComponent(handle)}`;
        url.search = location.search;
        return url.pathname + url.search;
      };
      // The theme editor can omit fragments from both href and location.hash.
      // Retain the selected route in its history entry as a preview fallback.
      const historyPath = url => url.pathname + url.search + url.hash;
      const navigationHash = () => location.hash || (window.Shopify?.designMode ? history.state?.wikiHash || '' : '');
      const routeResponse = await fetch(root.dataset.routesUrl);
      if (!routeResponse.ok) throw new Error('historical-routes');
      const routes = await routeResponse.json();
      const keyHandle = key => byHandle.has(key) ? key : ['r3-','r1-','wiki-'].map(prefix=>prefix+key).find(h=>byHandle.has(h));
      const pathNode = () => nodes.find(n => pageUrl(n)?.pathname === location.pathname);
      function routeHash(hash) {
        if (!hash || hash === '#') return [pathNode()?.handle || (location.pathname === initialPath ? initialHandle : 'overview')];
        let raw;
        try {raw=hash.replace(/^#/,'').split('/').map(decodeURIComponent);} catch {return ['overview'];}
        if(raw[0]==='wiki'){
          raw=raw.slice(1);
          if(!routes.aliases[raw[0]])return raw;
        }
        const pair=raw.slice(0,2).join('/');
        const key=routes.aliases[pair] || routes.aliases[raw[0]];
        if(key)return [keyHandle(key)||key,...raw.slice(routes.aliases[pair]?2:1)];
        // A body anchor entered on the current article remains inside that article.
        const currentArticle=pathNode();
        if(raw[0] && currentArticle)return [currentArticle.handle,raw.join('/')];
        return [raw[0]?'unavailable':'overview'];
      }
      const children = parent => nodes.filter(n => n.parent === parent).sort((a,b) => Number(a.sort)-Number(b.sort) || (a.handle < b.handle ? -1 : a.handle > b.handle ? 1 : 0));
      const tdsRoot = nodes.find(n => n.handle === 'r1-tds' && n.module === 'documents' && n.kind === 'group');
      const isTdsMaterial = n => !!tdsRoot && n?.parent === tdsRoot.handle && n.module === 'documents' && n.kind === 'group';
      const isCertificate = n => n?.module === 'documents' && n.kind === 'group' && ['r1-reach','r1-rohs','r1-sds-msds'].includes(n.handle);
      const certificateFiles = handle => children(handle).filter(n => n.kind === 'download'
        && n.template.content.querySelector('.wiki-download span')?.textContent.trim().toUpperCase() === 'PDF'
        && n.template.content.querySelector('a[data-download]')?.getAttribute('href')?.trim());
      const tdsFiles = handle => children(handle).filter(n => n.kind === 'download'
        && n.template.content.querySelector('.wiki-download span')?.textContent.trim().toUpperCase() === 'PDF'
        && n.template.content.querySelector('a[data-download]')?.getAttribute('href')?.trim());
      const moduleChildren = module => module.moduleNode ? children(module.handle) : children('').filter(n => n.module === module.handle && n.handle !== module.handle);
      const ancestors = n => {const result=[]; while(n?.parent){n=byHandle.get(n.parent); if(n) result.unshift(n);} return result;};
      const knowledgeEntries = () => moduleChildren(modules.find(m=>m.handle==='knowledge')).filter(n=>
        ['v11-part-1','v11-part-2','v11-part-3','r3-articles'].includes(n.handle)
        && (n.handle!=='v11-part-3' || nodes.some(article=>article.kind==='article'
          && (pageUrl(article) || article.template.content.querySelector('[data-article-url]'))
          && ancestors(article).some(parent=>parent.handle===n.handle))));
      const navigationHandle = handle => {
        const n=byHandle.get(handle);
        if(n?.module==='knowledge') return [...ancestors(n),n].find(item=>knowledgeEntries().some(entry=>entry.handle===item.handle))?.handle || 'knowledge';
        if(n?.module==='documents') return [...ancestors(n),n].find(item=>moduleChildren(modules.find(m=>m.handle==='documents')).some(entry=>entry.handle===item.handle))?.handle || 'documents';
        return handle;
      };
      const profileContext = node => {
        if (!node || node.module !== 'profiles') return null;
        const chain = [...ancestors(node), node];
        const brand = chain.find(item => !item.parent);
        if (!brand) return null;
        const model = chain.find(item => item.parent === brand.handle && item.kind === 'group') || null;
        const file = model && node.kind === 'download' && node.parent === model.handle ? node : null;
        return {brand, model, file};
      };
      const storageKey = 'cailab-wiki:directory';
      let expanded = new Set();
      try {expanded = new Set(JSON.parse(sessionStorage.getItem(storageKey) || '[]'));} catch { /* storage optional */ }
      const save = (writeHistory=true) => {
        const state = {wikiExpanded:[...expanded]};
        if(writeHistory)history.replaceState({...history.state,...state},'');
        try {sessionStorage.setItem(storageKey,JSON.stringify(state.wikiExpanded));} catch { /* storage optional */ }
      };
      const initialPath = location.pathname;
      let current = initialHandle;
      let request;
      let renderVersion = 0;
      let renderedUrl = '';
      let loadedArticle;
      const nav = root.querySelector('[data-wiki-nav]');
      const menu = root.querySelector('.wiki-menu');
      const backdrop=el('button',null,{class:'wiki-menu-backdrop',type:'button','aria-label':root.dataset.closeMenuLabel || 'Close navigation'});
      backdrop.hidden=true;root.append(backdrop);
      const closeButton=el('button','×',{class:'wiki-directory-close',type:'button','aria-label':root.dataset.closeMenuLabel || 'Close navigation'});
      root.querySelector('.wiki-sidebar').prepend(closeButton);
      root.querySelector('.wiki-sidebar').append(el('p','CAI LAB Wiki',{class:'wiki-sidebar-footer'}));
      const toc = root.querySelector('[data-wiki-toc]');
      const tocNav = toc?.querySelector('[data-wiki-toc-nav]');
      const tocDetails = toc?.querySelector('details');
      const desktopLayout = window.matchMedia('(min-width: 1024px)');
      const setStickyOffset = () => {
        root.style.setProperty('--wiki-page-top', `${Math.max(0, root.getBoundingClientRect().top + window.scrollY)}px`);
        const header = [...document.querySelectorAll('header,[role="banner"],[id*="header"],[class*="header"]')]
          .filter(item => !root.contains(item))
          .filter(item => {
            const rect = item.getBoundingClientRect();
            const position = getComputedStyle(item).position;
            return rect.width > innerWidth * 0.6 && rect.height > 32 && rect.height < 280 && (position === 'sticky' || position === 'fixed');
          })
          .sort((a,b) => b.getBoundingClientRect().bottom - a.getBoundingClientRect().bottom)[0];
        const headerBottom = header?.getBoundingClientRect().bottom || 0;
        root.style.setProperty('--wiki-sticky-top', `${Math.max(16, Math.min(200, Math.ceil(headerBottom + 12)))}px`);
      };
      const syncTocDisclosure = () => {if (tocDetails) tocDetails.open = desktopLayout.matches;};
      const clearCategoryArticles = () => {
        if (toc) toc.hidden = true;
        tocNav?.replaceChildren();
        root.removeAttribute('data-has-toc');
      };
      const prepareArticleAnchors = () => {
        const headings = [...main.querySelectorAll('.wiki-article h2,.wiki-article h3')];
        const usedIds = new Set([...document.querySelectorAll('[id]')].map(item => item.id).filter(Boolean));
        headings.forEach(heading => {
          removePartPrefix(heading);
          if (!heading.id) {
            const base = displayTitle(heading.textContent).normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu,'-').replace(/^-|-$/g,'') || 'section';
            let id = base, suffix = 2;
            while (usedIds.has(id)) id = `${base}-${suffix++}`;
            heading.id = id;
          }
          usedIds.add(heading.id);
        });
      };
      const updateCategoryArticles = () => {
        clearCategoryArticles();
        const article = byHandle.get(current);
        if (!toc || !tocNav || article?.kind !== 'article' || !article.parent || !main.querySelector('.wiki-article')) return;
        const items = children(article.parent).filter(item => item.kind === 'article' && item.module === article.module
          && (pageUrl(item) || item.template.content.querySelector('[data-article-url]')));
        if (!items.length) return;
        const list = el('ul',null,{class:'wiki-toc-list'});
        items.forEach(item => {
          const link = el('a',displayTitle(item.title),{href:href(item.handle),class:'wiki-toc-link','data-category-article':item.handle});
          if (item.handle === current) link.setAttribute('aria-current','page');
          const row = el('li',null,{class:'wiki-toc-item'});
          row.append(link);
          list.append(row);
        });
        tocNav.replaceChildren(list);
        toc.hidden = false;
        root.setAttribute('data-has-toc','true');
        syncTocDisclosure();
      };
      setStickyOffset();
      new ResizeObserver(setStickyOffset).observe(document.body);
      syncTocDisclosure();
      window.addEventListener('resize',setStickyOffset,{passive:true});
      desktopLayout.addEventListener?.('change',syncTocDisclosure);
      const closeMenu = () => {root.removeAttribute('data-menu-open'); backdrop.hidden=true;menu.setAttribute('aria-expanded','false');};
      menu.addEventListener('click',()=>{const open = !root.hasAttribute('data-menu-open'); root.toggleAttribute('data-menu-open',open);backdrop.hidden=!open;menu.setAttribute('aria-expanded',String(open));if(open)closeButton.focus();});
      backdrop.addEventListener('click',()=>{closeMenu();menu.focus();});
      closeButton.addEventListener('click',()=>{closeMenu();menu.focus();});
      root.addEventListener('keydown',e=>{if(e.key==='Escape'){closeMenu(); menu.focus();}});
      function tree() {
        const sidebar = root.querySelector('.wiki-sidebar');
        const scrollTop = sidebar.scrollTop;
        const ul = el('ul',null,{class:'wiki-tree'});
        function row(n, sub, parent) {
          if (n.module) sub = [];
          const li=el('li',null), line=el('div',null,{class:'wiki-tree-row'});
          const title=displayTitle(n.title);
          const link=el('a',null,{href:href(n.handle),'data-nav-handle':n.handle});
          if (['overview','knowledge','profiles','documents'].includes(n.handle)) link.append(icon(n.handle));
          link.append(el('span',title));
          if(navigationHandle(current)===n.handle) link.setAttribute('aria-current','page');
          if(sub.length){
            const open=expanded.has(n.handle);
            const button=el('button','›',{type:'button',class:'wiki-arrow','aria-label':`Toggle ${title}`,'aria-expanded':String(open)});
            if (open) line.setAttribute('data-expanded','true');
            button.addEventListener('click',()=>{openToggle(n.handle);});
            line.append(button);
            const branch=el('ul',null); branch.hidden=!open;
            sub.forEach(child=>row(child,children(child.handle),branch));
            li.append(line,branch);
          }else{line.append(el('span',null,{class:'wiki-leaf','aria-hidden':'true'}));li.append(line);}
          line.append(link); parent.append(li);
        }
        row({handle:'overview',title:'Overview'},[],ul);
        modules.forEach(m=>row(m,m.handle==='knowledge'?knowledgeEntries():moduleChildren(m),ul));
        nav.replaceChildren(ul);
        sidebar.scrollTop = scrollTop;
      }
      function openToggle(handle){
        expanded.has(handle)?expanded.delete(handle):expanded.add(handle); save();
        // Keep the focused arrow in place while toggling its own branch.
        const link=[...nav.querySelectorAll('[data-nav-handle]')].find(a=>a.dataset.navHandle===handle);
        const button=link.previousElementSibling;
        button.setAttribute('aria-expanded',String(expanded.has(handle)));
        link.closest('.wiki-tree-row').toggleAttribute('data-expanded',expanded.has(handle));
        link.closest('li').querySelector(':scope > ul').hidden=!expanded.has(handle);
      }
      function updateTree() {
        if (!nav.firstElementChild) tree();
        nav.querySelectorAll('[data-nav-handle]').forEach(link => {
          const handle = link.dataset.navHandle;
          if (handle === navigationHandle(current)) link.setAttribute('aria-current','page');
          else link.removeAttribute('aria-current');
          const branch = link.closest('li').querySelector(':scope > ul');
          if (branch) {
            branch.hidden = !expanded.has(handle);
            link.previousElementSibling.setAttribute('aria-expanded',String(expanded.has(handle)));
            link.closest('.wiki-tree-row').toggleAttribute('data-expanded',expanded.has(handle));
          }
        });
      }
      function cards(items) {
        const grid=el('div',null,{class:'wiki-grid'});
        items.forEach(item=>{
          const card=el('a',null,{class:'wiki-card',href:href(item.handle)});
          const mark=el('span',null,{class:'wiki-card-icon','aria-hidden':'true'});
          mark.append(icon(item.module || item.handle));
          const action=el('span',null,{class:'wiki-card-action','aria-hidden':'true'});
          action.append(icon('arrow'));
          card.append(mark,el('strong',displayTitle(item.title)),action);
          grid.append(card);
        });
        main.append(grid);
      }
      function list(items, heading) {
        if(!items.length)return;
        const browse=main.dataset.view==='browse';
        if(heading && !browse)main.append(el('h2',heading));
        const ul=el('ul',null,{class:'wiki-list'});
        items.forEach((n,index)=>{
          const li=el('li',null),a=el('a',null,{href:href(n.handle)});
          if(browse)a.append(el('span',String(index+1).padStart(2,'0'),{class:'wiki-list-index','aria-hidden':'true'}));
          a.append(el('span',displayTitle(n.title),{class:'wiki-list-title'}));
          if(browse){
            const action=el('span',null,{class:'wiki-list-action','aria-hidden':'true'});
            action.append(icon('arrow'));a.append(action);
          }else a.append(el('small',n.kind==='download'?'View file ↗':'View →'));
          li.append(a);ul.append(li);
        });
        main.append(ul);
      }
      function renderProfile(brand, requestedModel, requestedFile) {
        const models = children(brand.handle).filter(item => item.kind === 'group');
        const model = models.find(item => item.handle === requestedModel?.handle) || models[0];
        const tabs = el('nav',null,{class:'wiki-profile-tabs','aria-label':brand.title + ' printer models'});
        models.forEach(item => {
          const link = el('a',item.title,{href:href(item.handle),class:'wiki-profile-tab'});
          if (model?.handle === item.handle) link.setAttribute('aria-current','page');
          tabs.append(link);
        });
        main.append(tabs);
        const selectedTab=tabs.querySelector('[aria-current="page"]');
        if(selectedTab)tabs.scrollLeft=Math.max(0,selectedTab.offsetLeft-tabs.offsetLeft-17);
        if (!model) {
          main.append(el('p','No printer models are available for this brand yet.',{class:'wiki-notice'}));
          return;
        }
        main.append(el('h2',model.title,{class:'wiki-profile-model'}));
        const files = children(model.handle).filter(item => item.kind === 'download');
        if (!files.length) {
          main.append(el('p','No profiles are available for this model yet.',{class:'wiki-notice'}));
          return;
        }
        const table = el('table',null,{class:'wiki-profile-table'});
        table.append(el('caption','Downloads for ' + model.title,{class:'wiki-visually-hidden'}));
        const head = el('thead',null), headRow = el('tr',null);
        headRow.append(el('th','Format',{scope:'col'}),el('th','Download',{scope:'col'}));
        head.append(headRow);
        const body = el('tbody',null);
        files.forEach(file => {
          const row = el('tr',null,{'data-profile-file':file.handle});
          if (requestedFile?.handle === file.handle) row.setAttribute('data-current','true');
          const format = file.template.content.querySelector('.wiki-download span')?.textContent.trim() || '—';
          const formatCell=el('td',null,{class:'wiki-profile-format'});
          formatCell.append(el('span',format,{class:'wiki-profile-format-label'}));row.append(formatCell);
          const downloadCell = el('th',null,{class:'wiki-profile-download-cell',scope:'row'});
          const fileBox=el('div',null,{class:'wiki-profile-file'});
          fileBox.append(el('span',file.title,{class:'wiki-profile-file-label'}));
          downloadCell.append(fileBox);
          const sourceLink = file.template.content.querySelector('a[data-download]');
          const downloadUrl = sourceLink?.getAttribute('href');
          if (downloadUrl) {
            const link = el('a','Download',{href:downloadUrl,class:'wiki-profile-download','aria-label':'Download ' + file.title + ' profile for ' + model.title});
            const target = sourceLink.getAttribute('target');
            const rel = sourceLink.getAttribute('rel');
            if (target) link.setAttribute('target',target);
            if (rel) link.setAttribute('rel',rel);
            fileBox.append(link);
          } else {
            fileBox.append(el('span','Unavailable',{class:'wiki-profile-unavailable'}));
          }
          row.append(downloadCell);
          body.append(row);
        });
        table.append(head,body);
        const wrap = el('div',null,{class:'wiki-profile-table-wrap'});
        wrap.append(table);
        main.append(wrap);
      }
      function renderTds(node, content) {
        const filesLabel = root.dataset.tdsFilesLabel;
        const article = content.querySelector('.wiki-article');
        if (article) main.append(article);
        if (node === tdsRoot) {
          const grid = el('div',null,{class:'wiki-tds-grid'});
          children(node.handle).filter(isTdsMaterial).forEach(material => {
            const files = tdsFiles(material.handle);
            if (!files.length) return;
            const card = el('a',null,{class:'wiki-tds-card',href:href(material.handle),'data-tds-material':material.handle});
            const icon = el('span','PDF',{class:'wiki-tds-icon','aria-hidden':'true'});
            const body = el('span',null,{class:'wiki-tds-card-body'});
            body.append(el('strong',material.title),el('span',`${filesLabel}: ${files.length}`,{class:'wiki-tds-meta'}),el('span',null,{class:'wiki-tds-action','aria-hidden':'true'}));
            body.querySelector('.wiki-tds-action').append(el('span','→'));
            card.append(icon,body);grid.append(card);
          });
          if (grid.childElementCount) main.append(grid);
        }
        const files = node === tdsRoot ? children(node.handle).filter(n => n.kind === 'download'
          && n.template.content.querySelector('a[data-download]')?.getAttribute('href')?.trim()) : tdsFiles(node.handle);
        if (!files.length) return;
        const heading = el('div',null,{class:'wiki-tds-files-heading'});
        heading.append(el('h2',filesLabel),el('span',String(files.length),{class:'wiki-tds-meta'}));
        main.append(heading);
        const list = el('ul',null,{class:'wiki-tds-files'});
        files.forEach(file => {
          const row = el('li',null,{class:'wiki-tds-file'});
          const link = file.template.content.querySelector('a[data-download]').cloneNode(true);
          link.className = 'wiki-tds-download';
          const title = el('span',file.title,{class:'wiki-tds-file-title'});
          row.append(el('span',file.template.content.querySelector('.wiki-download span')?.textContent.trim() || '',{class:'wiki-tds-icon','aria-hidden':'true'}),title,link);
          list.append(row);
        });
        main.append(list);
      }
      function renderCertificateCards(items) {
        const grid=el('div',null,{class:'wiki-tds-grid'});
        items.forEach(node=>{
          const card=el('a',null,{class:'wiki-tds-card',href:href(node.handle),'data-document-category':node.handle});
          const body=el('span',null,{class:'wiki-tds-card-body'});
          body.append(el('strong',displayTitle(node.title)));
          if(isCertificate(node)) body.append(el('span',`${root.dataset.tdsFilesLabel}: ${certificateFiles(node.handle).length}`,{class:'wiki-tds-meta'}));
          body.append(el('span',null,{class:'wiki-tds-action','aria-hidden':'true'}));
          body.querySelector('.wiki-tds-action').append(icon('arrow'));
          card.append(el('span','PDF',{class:'wiki-tds-icon','aria-hidden':'true'}),body);grid.append(card);
        });
        if(grid.childElementCount) main.append(grid);
      }
      function renderCertificate(node,content) {
        const article=content.querySelector('.wiki-article');
        if(article) main.append(article);
        const files=certificateFiles(node.handle);
        const heading=el('div',null,{class:'wiki-tds-files-heading'});
        heading.append(el('h2',root.dataset.tdsFilesLabel),el('span',String(files.length),{class:'wiki-tds-meta'}));
        main.append(heading);
        if(!files.length) {main.append(el('p',root.dataset.documentsEmptyLabel,{class:'wiki-notice'}));return;}
        const list=el('ul',null,{class:'wiki-tds-files'});
        files.forEach(file=>{
          const row=el('li',null,{class:'wiki-tds-file'});
          const link=file.template.content.querySelector('a[data-download]').cloneNode(true);
          link.className='wiki-tds-download';
          row.append(el('span',file.template.content.querySelector('.wiki-download span').textContent.trim(),{class:'wiki-tds-icon','aria-hidden':'true'}),el('span',file.title,{class:'wiki-tds-file-title'}),link);
          list.append(row);
        });
        main.append(list);
      }
      function revealHandle(handle){
        const node=byHandle.get(handle);
        if(node){expanded.add(node.module);ancestors(node).forEach(p=>expanded.add(p.handle));}
        if((node && children(node.handle).length) || modules.some(m=>m.handle===handle))expanded.add(handle);
      }
      async function render(focus=false, reveal=false, writeHistory=true) {
        const version = ++renderVersion;
        clearCategoryArticles();
        request?.abort();
        request = null;
        main.removeAttribute('aria-busy');
        let segments;
        const hash=navigationHash();
        segments=routeHash(hash);
        const requested=segments[0];
        const linkedPage = pageUrl(byHandle.get(requested));
        if (linkedPage && (linkedPage.pathname !== location.pathname || hash.startsWith('#wiki/'))) {
          linkedPage.search = location.search;
          linkedPage.hash = segments[1] ? encodeURIComponent(segments.slice(1).join('/')) : '';
          history.replaceState({...history.state,wikiHash:linkedPage.hash},'',historyPath(linkedPage));
        }
        // Legacy articles keep their native URLs even when their directory is hidden.
        const legacyArticle=routes.articles.find(a=>a.key===requested || a.handles?.includes(requested));
        if(legacyArticle){
          const target=new URL(legacyArticle.sourceUrl,location.href);
          if(target.protocol==='https:' && /^\/blogs\/[^/]+\/[^/]+\/?$/.test(target.pathname)){
            const anchor=segments.slice(1).join('/');
            target.hash=anchor && legacyArticle.anchors?.includes(anchor)?encodeURIComponent(anchor):'';
            location.replace(target.href);
            return;
          }
        }
        const n=byHandle.get(requested), mod=modules.find(m=>m.handle===requested);
        const profile=profileContext(n);
        const displayNode=profile?.brand || n;
        renderedUrl=location.href;
        current=displayNode?.handle || mod?.handle || 'overview';
        if(reveal || !Array.isArray(history.state?.wikiExpanded)){
          revealHandle(current);
        }
        save(writeHistory);updateTree();main.replaceChildren();main.classList.remove('wiki-tds');
        main.dataset.view=current==='overview' || mod || (n?.module==='knowledge' && ['group','series'].includes(n.kind)) ? 'browse' : 'reading';
        main.classList.toggle('wiki-download-page',!!profile?.brand || (mod?.handle || n?.module)==='documents');
        const crumbs=el('nav',null,{class:'wiki-breadcrumb','aria-label':'Breadcrumb'});
        crumbs.append(el('a','Overview',{href:href('overview')}));
        const trail=displayNode?[modules.find(m=>m.handle===displayNode.module),...ancestors(displayNode),displayNode]:mod?[mod]:[];
        trail.filter((p,index,items)=>p && items.findIndex(item=>item?.handle===p.handle)===index).forEach(p=>{crumbs.append(el('span','/',{'aria-hidden':'true'}),el('a',displayTitle(p.title),{href:href(p.handle)}));});
        crumbs.lastElementChild.setAttribute('aria-current','page');main.append(crumbs);
        if(current==='overview'){
          if(requested && requested!=='overview')main.append(el('p','Content unavailable.',{class:'wiki-notice'}));
          const hero=el('div',null,{class:'wiki-hero'});hero.append(el('h1','CAI LAB Wiki'));main.append(hero);
          cards(modules);
        }else if(mod){
          main.append(el('h1',displayTitle(mod.title)));
          const items=mod.handle==='knowledge'?knowledgeEntries():moduleChildren(mod);
          if(mod.handle==='documents') {
            renderCertificateCards(items.filter(item=>isCertificate(item) || item===tdsRoot));
            list(items.filter(item=>!isCertificate(item) && item!==tdsRoot));
          } else if(mod.handle==='knowledge')cards(items);
          else list(items);
        }
        else if(profile?.brand){
          main.append(el('h1',displayTitle(profile.brand.title)));
          renderProfile(profile.brand,profile.model,profile.file);
        }
        else {
          main.append(el('h1',displayTitle(n.title)));
          let content=n.template.content.cloneNode(true);
          if (loadedArticle?.handle===n.handle) content=loadedArticle.content.cloneNode(true);
          if (pageUrl(n) && !content.querySelector('[data-page-url]')) {
            const loading=el('p','Loading article…',{role:'status',class:'wiki-notice'});
            main.append(loading);
            main.setAttribute('aria-busy','true');
            document.title=`${displayTitle(n.title)} · CAI LAB Wiki`;
            closeMenu();
            const controller=new AbortController();
            request=controller;
            const target=pageUrl(n);
            target.search=location.search;
            try {
              const response=await fetch(target.href,{credentials:'same-origin',signal:controller.signal});
              if (!response.ok || (response.url && new URL(response.url).href !== target.href)) throw new Error('article');
              const doc=new DOMParser().parseFromString(await response.text(),'text/html');
              const source=[...doc.querySelectorAll('[data-wiki][data-article-page="true"]')].find(section=>section.dataset.pageId===n.pageId);
              const entry=source && [...source.querySelectorAll('template[data-wiki-node]')].find(t=>t.dataset.wikiNode===n.handle && t.dataset.pageId===n.pageId && t.dataset.visible==='true');
              if (!entry?.content.querySelector('[data-page-url]')) throw new Error('article');
              if (version!==renderVersion) return;
              content=entry.content.cloneNode(true);
              loadedArticle={handle:n.handle,content:content.cloneNode(true)};
              loading.remove();
            } catch(error) {
              if (version!==renderVersion || error.name==='AbortError') return;
              loading.remove();
              const notice=el('div',null,{class:'wiki-error',role:'alert'});
              const retry=el('button','Retry',{type:'button'});
              retry.addEventListener('click',()=>render(true,false,false));
              const direct=el('a','Open article page',{href:target.href,'data-wiki-full-page':''});
              notice.append(el('p','Unable to load this article. Please try again.'),retry,el('span',' '),direct);
              main.append(notice);
              return;
            } finally {
              if (version===renderVersion) {main.removeAttribute('aria-busy');request=null;}
            }
          }
          content.querySelectorAll('[data-related]').forEach(x=>x.remove());
          if(n.kind==='series' && !n.template.content.querySelector('[data-article-url]') && !children(n.handle).some(c=>c.kind==='article' && c.template.content.querySelector('[data-article-url]'))){main.append(el('p','No articles yet.',{class:'wiki-notice'}));}
          if (n === tdsRoot || isTdsMaterial(n)) {
            main.classList.add('wiki-tds');
            renderTds(n,content);
          } else if (isCertificate(n)) {
            main.classList.add('wiki-tds');
            renderCertificate(n,content);
          } else if (n.module === 'documents' && n.kind === 'group') {
            main.append(content);
            const items = children(n.handle);
            list(items.filter(item => item.kind !== 'download'));
            const downloads = el('ul',null,{class:'wiki-list'});
            items.filter(item => item.kind === 'download').forEach(item => {
              const source = item.template.content.querySelector('a[data-download][href]');
              if (!source?.getAttribute('href')?.trim()) return;
              const link = source.cloneNode(true);
              link.replaceChildren(el('span',item.title));
              const row = el('li',null);
              row.append(link);downloads.append(row);
            });
            if (downloads.childElementCount) main.append(downloads);
          } else {
            main.append(content);
            list(children(n.handle),children(n.handle).length?'Contents':null);
          }
          const related=[...n.template.content.querySelectorAll('[data-related]')].map(r=>byHandle.get(r.dataset.related)).filter(r=>r?.kind==='download');
          list(related,'Resources');
        }
        if(main.dataset.view!=='browse' && !main.classList.contains('wiki-download-page'))main.append(el('p','CAI LAB / Knowledge & Resources',{class:'wiki-footnote'}));
        document.title=(current==='overview'?'Overview':displayTitle(displayNode?.title || mod?.title)) + ' · CAI LAB Wiki';
        prepareArticleAnchors();
        updateCategoryArticles();
        closeMenu(); if(focus) main.focus({preventScroll:true});
        if(segments[1] && n){const target=[...main.querySelectorAll('[id]')].find(t=>t.id===segments.slice(1).join('/'));target?.scrollIntoView({behavior:'smooth',block:'start'});}
        if(profile?.file){
          const target=[...main.querySelectorAll('[data-profile-file]')].find(row=>row.dataset.profileFile===profile.file.handle);
          target?.scrollIntoView({block:'center'});
        }
      }
      root.addEventListener('click',event=>{
        const a=event.target.closest('a');
        if(!a || event.defaultPrevented || event.button!==0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || a.hasAttribute('download') || a.hasAttribute('data-download') || a.hasAttribute('data-wiki-full-page') || (a.target && a.target!=='_self'))return;
        const url=new URL(a.href,location.href);
        if(url.origin!==location.origin)return;
        const article=nodes.find(n=>pageUrl(n)?.pathname===url.pathname);
        if (article && !url.hash.startsWith('#wiki/') && url.search===location.search) {
          if (url.pathname===location.pathname && url.hash && a.closest('.wiki-article')) return;
          event.preventDefault();
          save();
          history.pushState({wikiExpanded:[...expanded],wikiHash:url.hash},'',historyPath(url));
          render(true,false,false);
          if (!url.hash) root.scrollIntoView({block:'start'});
          return;
        }
        if(url.origin!==location.origin || url.pathname!==location.pathname || url.search!==location.search)return;
        if(a.closest('.wiki-article') && url.hash && !url.hash.startsWith('#wiki/'))url.hash=`wiki/${encodeURIComponent(current)}/${encodeURIComponent(url.hash.slice(1))}`;
        if(!url.hash.startsWith('#wiki/')){
          const target=routeHash(url.hash);
          if(!target[0] || target[0]==='unavailable')return;
          url.hash=`wiki/${target.map(encodeURIComponent).join('/')}`;
        }
        event.preventDefault();
        try {revealHandle(decodeURIComponent(url.hash.split('/')[1]));} catch { /* invalid route falls back to Overview */ }
        save();history.pushState({wikiExpanded:[...expanded],wikiHash:url.hash},'',historyPath(url));render(true,false,false);
        if(!url.hash.split('/')[2])root.scrollIntoView({block:'start'});
      });
      window.addEventListener('popstate',()=>{if(Array.isArray(history.state?.wikiExpanded))expanded=new Set(history.state.wikiExpanded);render(true,false,false);});
      window.addEventListener('hashchange',()=>{
        if (location.href!==renderedUrl) render(true,false,!Array.isArray(history.state?.wikiExpanded));
      });
      render();root.dataset.ready='true';root.dataset.loaded=String(all.size);
    }catch(error){main.replaceChildren(el('p','Unable to load. Refresh to retry.',{class:'wiki-error',role:'alert'}));root.dataset.error=error.message;}
  }
  document.querySelectorAll('[data-wiki]').forEach(init);
  document.addEventListener('shopify:section:load',e=>e.target.querySelectorAll('[data-wiki]').forEach(init));
})();
